import { useEffect, useState } from "react";
import { clsx } from "clsx";
import { GripVertical, Loader2, RotateCcw, Save } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { pageImageUrl } from "../api/pdfApi";
import type { PdfDocument } from "../types";

/** 1 strona / 2 strony / 5 stron — bez tego licznik czyta się jak błąd. */
export function pagesLabel(n: number): string {
  if (n === 1) return "1 strona";
  const last = n % 10;
  const teen = n % 100 >= 12 && n % 100 <= 14;
  return `${n} ${!teen && last >= 2 && last <= 4 ? "strony" : "stron"}`;
}

/**
 * Miniatury stron: zaznaczanie (klik, Ctrl, Shift) i ZMIANA KOLEJNOŚCI
 * przeciąganiem. Zaznaczenie wpada do parametru „strony” każdego narzędzia, więc
 * nikt nie przepisuje numerów ręcznie — a kolejność ustawia się tak, jak człowiek
 * przekłada kartki, zamiast wpisywać „3,1,2” w pole tekstowe.
 */
export function PageGrid({
  document: doc,
  ticket,
  selected,
  onSelect,
  onOpenPage,
  onReorder,
  reordering,
}: {
  document: PdfDocument;
  ticket: string | undefined;
  selected: number[];
  onSelect: (pages: number[]) => void;
  onOpenPage?: (page: number) => void;
  /** Nowa kolejność: numery stron z oryginału w docelowej kolejności. */
  onReorder?: (order: number[]) => void;
  reordering?: boolean;
}) {
  const count = doc.page_count || 0;
  const natural = Array.from({ length: count }, (_, i) => i + 1);
  const [order, setOrder] = useState<number[]>(natural);
  const [dragged, setDragged] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  // Miniatury renderuje serwer (poppler) — przy pierwszym wejściu trwa to chwilę,
  // więc każda kafelka mówi wprost, że się buduje.
  const [ready, setReady] = useState<Set<number>>(new Set());

  useEffect(() => {
    setOrder(Array.from({ length: doc.page_count || 0 }, (_, i) => i + 1));
    setReady(new Set());
  }, [doc.id, doc.page_count, doc.version_number]);

  const markReady = (page: number) =>
    setReady((prev) => (prev.has(page) ? prev : new Set(prev).add(page)));

  const moved = order.length === natural.length && order.some((p, i) => p !== natural[i]);

  const click = (page: number, e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey) {
      onSelect(
        selected.includes(page)
          ? selected.filter((p) => p !== page)
          : [...selected, page].sort((a, b) => a - b),
      );
      return;
    }
    if (e.shiftKey && selected.length > 0) {
      const last = selected[selected.length - 1];
      const [a, b] = last < page ? [last, page] : [page, last];
      onSelect(Array.from({ length: b - a + 1 }, (_, i) => a + i));
      return;
    }
    onSelect(selected.length === 1 && selected[0] === page ? [] : [page]);
  };

  const drop = (source: number | null, target: number) => {
    if (source === null || source === target) return;
    setOrder((prev) => {
      // Przeciągnięcie W PRAWO wstawia ZA kartkę, w lewo PRZED nią — tak działa
      // każdy sorter slajdów i tak to czyta ręka, która przekłada kartki.
      const forward = prev.indexOf(source) < prev.indexOf(target);
      const next = prev.filter((p) => p !== source);
      next.splice(next.indexOf(target) + (forward ? 1 : 0), 0, source);
      return next;
    });
    setDragged(null);
    setOver(null);
  };

  if (!count) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-xs text-[var(--color-text-muted)]">
        Ten plik nie ma stron do pokazania ({doc.content_type || "nieznany typ"}).
        <br />
        Skorzystaj z konwersji, żeby zrobić z niego PDF.
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="section-label">
          {pagesLabel(count)}
          {selected.length > 0 && ` · zaznaczone: ${selected.join(", ")}`}
          {onReorder && !moved && selected.length === 0 &&
            " · przeciągnij miniaturę, żeby zmienić kolejność"}
        </span>
        <span className="flex items-center gap-2">
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onSelect([])}
              className="section-label hover:text-[var(--color-primary)]"
            >
              Wyczyść zaznaczenie
            </button>
          )}
          {onReorder && moved && (
            <>
              <Button type="button" variant="ghost" size="sm" onClick={() => setOrder(natural)}>
                <RotateCcw size={12} className="mr-1" /> Cofnij
              </Button>
              <Button
                type="button" size="sm" loading={reordering}
                onClick={() => onReorder(order)}
              >
                <Save size={12} className="mr-1" /> Zapisz kolejność
              </Button>
            </>
          )}
        </span>
      </div>

      <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-2.5">
        {order.map((page, index) => {
          const active = selected.includes(page);
          return (
            <div
              key={page}
              role="button"
              tabIndex={0}
              // Kafelek MUSI być divem: natywne przeciąganie nie startuje z <button>.
              draggable={!!onReorder}
              onDragStart={(e) => {
                setDragged(page);
                // Bez ładunku w dataTransfer Firefox w ogóle nie zaczyna
                // przeciągania, a Chromium potrafi odrzucić upuszczenie.
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", String(page));
              }}
              onDragEnd={() => {
                setDragged(null);
                setOver(null);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                setOver(page);
              }}
              onDragLeave={() => setOver((p) => (p === page ? null : p))}
              onDrop={(e) => {
                e.preventDefault();
                const source = Number(e.dataTransfer.getData("text/plain")) || dragged;
                drop(source, page);
              }}
              onClick={(e) => click(page, e)}
              onDoubleClick={() => onOpenPage?.(page)}
              onKeyDown={(e) => e.key === "Enter" && onSelect([page])}
              aria-pressed={active}
              aria-label={`Strona ${page}`}
              className={clsx(
                "group relative flex cursor-pointer flex-col items-center border bg-[var(--color-bg-surface)] p-1 transition-colors duration-100",
                active
                  ? "border-[var(--color-primary)] shadow-[3px_3px_0_var(--color-primary)]"
                  : "border-[var(--color-border)] hover:border-[var(--color-border-strong)]",
                over === page && dragged !== null && dragged !== page &&
                  (order.indexOf(dragged) < order.indexOf(page)
                    ? "border-r-[3px] border-r-[var(--color-primary)]"
                    : "border-l-[3px] border-l-[var(--color-primary)]"),
                dragged === page && "opacity-40",
              )}
            >
              {onReorder && (
                <GripVertical
                  size={12}
                  className="absolute left-1 top-1 z-10 text-[var(--color-border-strong)] opacity-0 transition-opacity group-hover:opacity-100"
                />
              )}
              {ticket ? (
                <span className="relative block w-full">
                  <img
                    src={pageImageUrl(doc.id, page, ticket, 220, doc.version_number)}
                    alt={`Strona ${page}`}
                    loading="lazy"
                    draggable={false}
                    onLoad={() => markReady(page)}
                    onError={() => markReady(page)}
                    className="h-auto w-full border border-[var(--color-border-muted)] bg-white"
                  />
                  {!ready.has(page) && (
                    <span
                      className="absolute right-1 top-1 flex items-center bg-[var(--color-bg-surface)] p-0.5"
                      title="Buduję podgląd strony"
                    >
                      <Loader2 size={11} className="animate-spin text-[var(--color-primary)]" />
                    </span>
                  )}
                </span>
              ) : (
                <div className="aspect-[1/1.41] w-full animate-pulse bg-[var(--color-bg-elevated)]" />
              )}
              <span
                className={clsx(
                  "mt-1 font-mono text-[10px]",
                  active ? "text-[var(--color-primary)]" : "text-[var(--color-text-muted)]",
                )}
              >
                {moved ? `${index + 1} ← ${page}` : page}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
